import type { UseFormWatch } from "react-hook-form";
import type {
  FacilityFormModel,
  FacilitySection,
} from "Components/Shared/Merchant/Facilities/schema";
import { isPhoneEmpty } from "Utils/PhoneUtils";

const isFilled = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  return true;
};

const WATCHED_FIELDS = [
  "name",
  "providedServices",
  "responsiblePerson",
  "phone",
  "email",
  "address",
  "yearlyTurnover",
  "realCardTurnover",
  "averageTransactionValue",
] as const satisfies ReadonlyArray<keyof FacilityFormModel>;

type PhotosState = {
  hasExistingOutside: boolean;
  hasExistingInside: boolean;
  hasPendingOutside: boolean;
  hasPendingInside: boolean;
};

export const useFacilitySectionCompletion = (
  watch: UseFormWatch<FacilityFormModel>,
  photos?: PhotosState,
): Record<FacilitySection, boolean> => {
  const [
    name,
    providedServices,
    responsiblePerson,
    phone,
    email,
    address,
    yearlyTurnover,
    realCardTurnover,
    averageTransactionValue,
  ] = watch(WATCHED_FIELDS);

  const hasPhotoOutside =
    photos?.hasExistingOutside || photos?.hasPendingOutside || false;
  const hasPhotoInside =
    photos?.hasExistingInside || photos?.hasPendingInside || false;

  return {
    basicInformation: [name, providedServices].every(isFilled),
    contact: [responsiblePerson, email].every(isFilled) && !isPhoneEmpty(phone),
    // Public info completion depends on isPublic toggle — not tracked as completable
    publicFacilityInfo: false,
    address: [
      address?.street,
      address?.streetNumber,
      address?.postalCode,
      address?.municipality,
    ].every(isFilled),
    turnover: [yearlyTurnover, realCardTurnover, averageTransactionValue].every(
      isFilled,
    ),
    // A SoftPOS-only facility has no physical count, so either count completes the section.
    terminals:
      isFilled(watch("terminalCount")) || isFilled(watch("softPosCount")),
    photos: hasPhotoOutside && hasPhotoInside,
  };
};
